import React, { useEffect, useState } from 'react'
import { Box, Toolbar, Typography, Button, Paper } from '@material-ui/core'
import { useForm, FormProvider, useFieldArray } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import ApiCopro from '../api'
import EmployeGardienRow from './EmployeGardienRow'
import { formatPhone, validatePhone } from '../../utils'
import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'

const schema = yup.object().shape({
  rows: yup.array().of(
    yup.object().shape({
      role: yup.string().required('Role is required'),
      nom: yup.string().when('role', {
        is: 'employe',
        then: yup.string().required('Nom Employé is required'),
        otherwise: yup.string().required('Nom Gardien est required'),
      }),
      email: yup
        .string()
        .email('Invalid email')
        .when('role', {
          is: 'employe',
          then: yup.string().required('Email Employé is required'),
          otherwise: yup.string().required('Email Gardien est required'),
        }),
      tel: yup
        .string()
        .required('Tel is required')
        .test(
          'is-valid-phone',
          'Numéro invalide. Format attendu [+][indicatif de pays][numero]',
          (value) => !value || validatePhone(value) === null
        )
        .when('role', {
          is: 'employe',
          then: yup.string().required('Tel Employé is required'),
          otherwise: yup.string().required('Tel Gardien est required'),
        }),
      logement: yup.string().when('role', {
        is: 'gardien',
        then: yup.string().required('Logement is required'),
      }),
      id: yup.string().nullable(),
    })
  ),
})

const EmployeGuardianForm = ({ id }) => {
  const [record, setRecord] = useState({})

  const apiCopro = new ApiCopro()
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      rows: [],
    },
  })
  const { control, handleSubmit, reset, formState } = methods
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'rows',
  })

  const fetchData = async () => {
    const res = await apiCopro.getCoproFiche(id)
    if (res.status === 200) {
      const data = res.data
      if (data) {
        const initialRows = []

        for (let i = 1; i <= 4; i++) {
          if (data[`fiche_gardien${i}_id`]) {
            initialRows.push({
              role: 'gardien',
              nom: data[`fiche_gardien${i}_nom`],
              email: data[`fiche_gardien${i}_email`],
              tel: data[`fiche_gardien${i}_tel`],
              logement: data[`fiche_gardien${i}_logement`] || '',
              id: data[`fiche_gardien${i}_id`] || uuidv4(),
            })
          }
          if (data[`fiche_employe${i}_id`]) {
            initialRows.push({
              role: 'employe',
              nom: data[`fiche_employe${i}_nom`],
              email: data[`fiche_employe${i}_email`],
              tel: data[`fiche_employe${i}_tel`],
              logement: '',
              id: data[`fiche_employe${i}_id`] || uuidv4(),
            })
          }
        }
        reset({ rows: initialRows })
        setRecord(data)
      }
    } else {
      setRecord({})
    }
  }

  useEffect(() => {
    fetchData()
  }, [id])

  const transformData = (data) => {
    const transformedData = {}
    let gardienIndex = 1
    let employeIndex = 1

    data.rows.forEach((row) => {
      if (row.role === 'gardien') {
        transformedData[`fiche_gardien${gardienIndex}_nom`] = row.nom
        transformedData[`fiche_gardien${gardienIndex}_email`] = row.email
        transformedData[`fiche_gardien${gardienIndex}_tel`] = formatPhone(
          row.tel
        )
        transformedData[`fiche_gardien${gardienIndex}_logement`] = row.logement
        transformedData[`fiche_gardien${gardienIndex}_id`] = row.id
        gardienIndex++
      } else if (row.role === 'employe') {
        transformedData[`fiche_employe${employeIndex}_nom`] = row.nom
        transformedData[`fiche_employe${employeIndex}_email`] = row.email
        transformedData[`fiche_employe${employeIndex}_tel`] = formatPhone(
          row.tel
        )
        transformedData[`fiche_employe${employeIndex}_id`] = row.id
        employeIndex++
      }
    })

    // Fill in the gaps for missing entries with empty strings
    for (let i = gardienIndex; i <= 4; i++) {
      transformedData[`fiche_gardien${i}_nom`] = null
      transformedData[`fiche_gardien${i}_email`] = null
      transformedData[`fiche_gardien${i}_tel`] = null
      transformedData[`fiche_gardien${i}_logement`] = null
      transformedData[`fiche_gardien${i}_id`] = null
    }
    for (let i = employeIndex; i <= 4; i++) {
      transformedData[`fiche_employe${i}_nom`] = null
      transformedData[`fiche_employe${i}_email`] = null
      transformedData[`fiche_employe${i}_tel`] = null
      transformedData[`fiche_employe${i}_id`] = null
    }

    transformedData['fiche_gardien'] = gardienIndex > 1 ? 'Oui' : 'Non'

    return transformedData
  }

  const onSubmit = async (data) => {
    const payload = transformData(data)

    try {
      const res = await apiCopro.updateCoproFiche(id, payload)
      if (res.status === 200) {
        fetchData()
        toast.success('Votre fiche copropriété a été modifiée avec succès !', {
          autoClose: 3000,
        })
      } else {
        toast.error(
          'Une erreur est survenue lors de la mise à jour de la fiche copropriété.',
          {
            autoClose: 3000,
          }
        )
      }
    } catch (error) {
      toast.error('Une erreur est survenue. Veuillez réessayer.', {
        autoClose: 3000,
      })
    }
  }

  return (
    <Paper elevation={3}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box p=".7em">
            <Box display="flex">
              <Box flex={1} mr="1em">
                <Typography variant="h6" style={{ padding: '0.5rem' }}>
                  <b>Employés et gardiens</b>
                </Typography>
                {fields.map((row, index) => (
                  <EmployeGardienRow
                    record={record}
                    key={row.id}
                    index={index}
                    control={control}
                    remove={remove}
                  />
                ))}
              </Box>
            </Box>
          </Box>
          <Toolbar>
            <Box display="flex" justifyContent="space-between" width="100%">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!formState.isDirty}
              >
                Enregistrer
              </Button>
              {fields.length < 4 && (
                <Button
                  onClick={() =>
                    append({
                      role: '',
                      nom: '',
                      email: '',
                      tel: '',
                      logement: '',
                      id: uuidv4(),
                    })
                  }
                  variant="contained"
                  color="primary"
                >
                  Ajouter un choix
                </Button>
              )}
            </Box>
          </Toolbar>
        </form>
      </FormProvider>
    </Paper>
  )
}

export default EmployeGuardianForm
